import {
  Component,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
  type ReactNode,
} from 'react';

/**
 * A page loaded on demand, with the two things `lazy` alone leaves out.
 *
 * Something to look at while the chunk comes down — `AuthPending`'s plain
 * line, because a blank under the header for the seconds a 300 kB chunk takes
 * on a slow link reads as broken — and somewhere to land when it does not
 * come down at all. That happens: a deploy between two navigations, and the
 * chunk the running page asks for by hash is gone from the server. `lazy`
 * throws, and without a boundary React unmounts the whole admin to a white
 * screen with nothing to press. Em Liên asked for this before the first
 * chunk existed.
 */
export function LazyPage({ page: Page }: { page: LazyExoticComponent<ComponentType> }) {
  return (
    <ChunkBoundary>
      <Suspense fallback={<Loading />}>
        <Page />
      </Suspense>
    </ChunkBoundary>
  );
}

function Loading() {
  return (
    <div
      className="grid min-h-[40vh] place-items-center font-mono text-xs"
      style={{ color: 'var(--muted)' }}
    >
      <span>Loading…</span>
    </div>
  );
}

/**
 * The one place the admin catches a render error, and only for a chunk that
 * failed to load: a class, because React has no hook for this yet. Nothing
 * is retried on its own — the fix for a stale chunk is a reload, which gets
 * the new page and the new chunk together.
 */
class ChunkBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        className="mx-auto flex max-w-[480px] flex-col items-center gap-3 px-6 py-16 text-center"
      >
        <span className="text-[14px] font-medium">This page didn’t load</span>
        <span className="text-[12.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
          A newer version of the admin may have just been published. Reloading gets the page and its
          code together.
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="focus-ring mt-1 cursor-pointer rounded-[8px] border px-3 py-1.5 text-[12.5px]"
          style={{ borderColor: 'var(--line2)', background: 'var(--in)' }}
        >
          Reload
        </button>
      </div>
    );
  }
}
